import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

const SEPARATION = 150;
const AMOUNTX = 40;
const AMOUNTY = 60;

function readTheme() {
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

export default function DottedSurface({ size = 5, opacity = 0.45 }) {
  const containerRef = useRef(null);
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setTheme(readTheme()));
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dark = theme === "dark";
    const fogColor = dark ? 0x07080a : 0xf6f6f3;
    const dot = dark ? [214 / 255, 242 / 255, 92 / 255] : [27 / 255, 77 / 255, 14 / 255];

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(fogColor, 2000, 10000);

    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 10000);
    camera.position.set(0, 355, 1220);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, failIfMajorPerformanceCaveat: false });
    } catch (error) {
      return undefined;
    }
    if (!renderer.getContext()) {
      renderer.dispose();
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(fogColor, 0);
    container.appendChild(renderer.domElement);

    const positions = [];
    const colors = [];
    for (let ix = 0; ix < AMOUNTX; ix += 1) {
      for (let iy = 0; iy < AMOUNTY; iy += 1) {
        positions.push(
          ix * SEPARATION - (AMOUNTX * SEPARATION) / 2,
          0,
          iy * SEPARATION - (AMOUNTY * SEPARATION) / 2,
        );
        colors.push(dot[0], dot[1], dot[2]);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size,
      vertexColors: true,
      transparent: true,
      opacity: dark ? opacity : Math.min(opacity + 0.3, 0.9),
      sizeAttenuation: true,
    });
    const points = new THREE.Points(geometry, material);
    scene.add(points);

    let frame = 0;
    let count = 0;
    const positionAttribute = geometry.attributes.position;

    const render = () => {
      if (!reduce) {
        const values = positionAttribute.array;
        let i = 0;
        for (let ix = 0; ix < AMOUNTX; ix += 1) {
          for (let iy = 0; iy < AMOUNTY; iy += 1) {
            values[i * 3 + 1] = Math.sin((ix + count) * 0.3) * 50 + Math.sin((iy + count) * 0.5) * 50;
            i += 1;
          }
        }
        positionAttribute.needsUpdate = true;
        count += 0.1;
      }
      renderer.render(scene, camera);
    };

    const animate = () => {
      frame = requestAnimationFrame(animate);
      render();
    };

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener("resize", onResize);
    if (reduce) render();
    else animate();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
    };
  }, [theme, size, opacity]);

  return <div ref={containerRef} className="dotted-surface" aria-hidden="true" />;
}
